import {
  Avatar, Flexbox, Form, ItemCol, LoadManager,
} from '@/components'
import { Button } from '@/components/styles'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import usePageTitle from '@/hooks/usePageTitle'
import { ANIMAL_FAIR } from '@/lib/enums'
import rga from '@/lib/masks/rga'
import animalFairs from '@/services/api/animalFairs'
import animals from '@/services/api/animals'
import React, { useCallback, useEffect, useState } from 'react'
import { confirmAlert } from 'react-confirm-alert'
import {
  Link, useHistory, useLocation, useParams,
} from 'react-router-dom'
import { toast } from 'react-toastify'
import QRCode from 'react-qr-code'
import envs from '@/constants/envs'
import CharacteristicTags from '../components/CharacteristicTags'

function toggleValue(values, value) {
  if (values.includes(value)) return values.filter((item) => item !== value)
  return [...values, value]
}

function Show({ title }) {
  usePageTitle({ title })
  const [removing, setRemoving] = useState(false)
  const [editingCharacteristics, setEditingCharacteristics] = useState(false)
  const [savingCharacteristics, setSavingCharacteristics] = useState(false)
  const [behaviorCharacteristics, setBehaviorCharacteristics] = useState([])
  const [healthConditions, setHealthConditions] = useState([])
  const [obs, setObs] = useState('')
  const { petId, id } = useParams()
  const history = useHistory()
  const location = useLocation()

  const {
    data, loading, error, fetchData,
  } = useAxiosRequest(animalFairs.show, petId)

  useEffect(() => {
    if (location.pathname) fetchData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    setBehaviorCharacteristics(data?.animal?.characteristics?.behaviorCharacteristics || [])
    setHealthConditions(data?.animal?.characteristics?.healthConditions || [])
    setObs(data?.animal?.characteristics?.obs || '')
    setEditingCharacteristics(false)
  }, [data])

  const onRemove = useCallback(async () => {
    try {
      setRemoving(true)
      await animalFairs.destroy(petId)
      toast.success('Animal removido da feira')
      history.goBack()
    } catch (e) {
      setRemoving(false)
      toast.error(e?.message)
    }
  }, [history, petId])

  const handleRemove = useCallback(async () => {
    confirmAlert({
      title: 'Remover pet',
      message: `Você está certo que deseja remover ${data?.animal?.name} da feira?`,
      buttons: [
        { label: 'Não' },
        {
          label: 'Sim',
          className: 'bg-error',
          onClick: onRemove,
        },
      ],
    })
  }, [data?.animal?.name, onRemove])

  const handleCancelCharacteristics = useCallback(() => {
    setBehaviorCharacteristics(data?.animal?.characteristics?.behaviorCharacteristics || [])
    setHealthConditions(data?.animal?.characteristics?.healthConditions || [])
    setObs(data?.animal?.characteristics?.obs || '')
    setEditingCharacteristics(false)
  }, [data])

  const handleSaveCharacteristics = useCallback(async () => {
    try {
      setSavingCharacteristics(true)
      await animals.updateCharacteristics(data?.animal?.id, {
        characteristics: {
          behaviorCharacteristics,
          healthConditions,
          obs,
        },
      })
      fetchData()
      setEditingCharacteristics(false)
      toast.success('Características atualizadas')
    } catch (e) {
      toast.error(e?.message)
    } finally {
      setSavingCharacteristics(false)
    }
  }, [
    behaviorCharacteristics, data?.animal?.id,
    fetchData, healthConditions, obs,
  ])

  return (
    <div className="modal active">
      <a type="button" className="modal-overlay" aria-label="Close" onClick={() => history.goBack()} />
      <div className="modal-container">
        <div className="modal-header">
          <a
            type="button"
            className="btn btn-clear float-right"
            onClick={() => history.goBack()}
          />
          <div className="modal-title h5">
            Detalhes do Pet
          </div>
        </div>
        <div className="modal-body">
          <LoadManager error={error} loading={loading}>
            {
              Boolean(data) && (
                <>
                  <Flexbox.Columns>
                    <Flexbox.Column className="col-auto">
                      <Avatar avatarSize="xl" name={data?.animal?.name} photoURL={data?.animal?.photoURL} />
                    </Flexbox.Column>
                    <Flexbox.Column>
                      <div className="h5">{data?.animal?.name}</div>
                      <div>
                        {data?.animal?.category?.description} - {data?.animal?.breed?.description}
                      </div>
                    </Flexbox.Column>
                  </Flexbox.Columns>
                  <Flexbox.Columns className="mt-2 pt-2">
                    <Flexbox.Column className="col-6">
                      <ItemCol label="RGA" value={rga(data?.animal?.uid)} />
                    </Flexbox.Column>
                    <Flexbox.Column className="col-6">
                      <ItemCol label="Adotado?" value={data?.adopted ? 'Sim' : 'Não'} />
                    </Flexbox.Column>
                  </Flexbox.Columns>
                  <Flexbox.Columns className="mt-2 pt-2">
                    {
                      editingCharacteristics ? (
                        <>
                          <Flexbox.Column className="col-12">
                            <Form.Label>Características de comportamento</Form.Label>
                          </Flexbox.Column>
                          {
                            Object.values(ANIMAL_FAIR.behaviorCharacteristics.enum).map((item) => (
                              <Flexbox.Column key={item} className="col-6 col-sm-12">
                                <Form.Check>
                                  <input
                                    type="checkbox"
                                    checked={behaviorCharacteristics.includes(item)}
                                    onChange={() => setBehaviorCharacteristics(
                                      toggleValue(behaviorCharacteristics, item),
                                    )}
                                  />
                                  <i className="form-icon" />
                                  {ANIMAL_FAIR.behaviorCharacteristics.t(item)}
                                </Form.Check>
                              </Flexbox.Column>
                            ))
                          }
                          <Flexbox.Column className="col-12 pt-2">
                            <Form.Label>Condições de saúde</Form.Label>
                          </Flexbox.Column>
                          {
                            Object.values(ANIMAL_FAIR.healthConditions.enum).map((item) => (
                              <Flexbox.Column key={item} className="col-6 col-sm-12">
                                <Form.Check>
                                  <input
                                    type="checkbox"
                                    checked={healthConditions.includes(item)}
                                    onChange={() => setHealthConditions(
                                      toggleValue(healthConditions, item),
                                    )}
                                  />
                                  <i className="form-icon" />
                                  {ANIMAL_FAIR.healthConditions.t(item)}
                                </Form.Check>
                              </Flexbox.Column>
                            ))
                          }
                          <Flexbox.Column className="col-12 pt-2">
                            <Form.Label>Observações</Form.Label>
                            <Form.Textarea
                              rows={3}
                              value={obs}
                              onChange={(event) => setObs(event.target.value)}
                            />
                          </Flexbox.Column>
                          <Flexbox.Column className="col-12 pt-2 text-right">
                            <button
                              type="button"
                              className="btn btn-link btn-sm"
                              disabled={savingCharacteristics}
                              onClick={handleCancelCharacteristics}
                            >
                              <i className="fas fa-times" />
                              &nbsp;Cancelar
                            </button>
                            <button
                              type="button"
                              className={`btn btn-primary btn-sm ${savingCharacteristics ? 'loading' : ''}`}
                              disabled={savingCharacteristics}
                              onClick={handleSaveCharacteristics}
                            >
                              <i className="fas fa-check" />
                              &nbsp;Salvar
                            </button>
                          </Flexbox.Column>
                        </>
                      ) : (
                        <>
                          <Flexbox.Column className="col-12">
                            <ItemCol
                              label="Características de comportamento"
                              value={(
                                <CharacteristicTags
                                  enumData={ANIMAL_FAIR.behaviorCharacteristics}
                                  values={behaviorCharacteristics}
                                />
                              )}
                            />
                          </Flexbox.Column>
                          <Flexbox.Column className="col-12 pt-2">
                            <ItemCol
                              label="Condições de saúde"
                              value={(
                                <CharacteristicTags
                                  enumData={ANIMAL_FAIR.healthConditions}
                                  values={healthConditions}
                                />
                              )}
                            />
                          </Flexbox.Column>
                          {
                            Boolean(obs) && (
                              <Flexbox.Column className="col-12 pt-2">
                                <ItemCol label="Observações" value={obs} />
                              </Flexbox.Column>
                            )
                          }
                          <Flexbox.Column className="col-12 pt-2">
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => setEditingCharacteristics(true)}
                            >
                              <i className="fas fa-pen" />
                              &nbsp;Editar características
                            </button>
                          </Flexbox.Column>
                        </>
                      )
                    }
                  </Flexbox.Columns>
                  <div className="my-2 py-2">
                    <QRCode
                      style={{ width: 150, height: 150 }}
                      value={`${envs.REACT_APP_BASE_WEB_SHOW_URL}/adoption-fairs/${id}/animals/${petId}`}
                    />
                  </div>
                </>
              )
            }
          </LoadManager>
        </div>
        <div className="modal-footer">
          {
            Boolean(data?.complaints) && (
              <Link to={`${petId}/complaints`} className="btn btn-link btn-sm">
                Exibir {`${data?.complaints} ${data?.complaints > 1 ? 'denúncias' : 'denúncia'}`}
              </Link>
            )
          }
          {
            !data?.adopted && (
              <Button onClick={handleRemove} disabled={removing} type="button" className={`btn-sm btn-error ml-2 ${removing ? 'loading' : ''}`}>
                <i className="fas fa-trash" />
                &nbsp;Remover da feira
              </Button>
            )
          }
        </div>
      </div>
    </div>
  )
}

export default Show
